import { useEffect, useRef } from "react";
import { postHeartbeat } from "@/api/presence";

const HEARTBEAT_INTERVAL_MS = 60_000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"] as const;

/**
 * Tracks real user presence and sends a heartbeat to the backend every 60 s
 * when the user has been active (click / keystroke / scroll).
 *
 * Heartbeats are deduplicated server-side by (user, device_id, minute) so
 * multiple browser tabs on the same device never double-count.
 */
export function usePresence() {
  const wasActiveRef = useRef(false);

  useEffect(() => {
    const markActive = () => {
      wasActiveRef.current = true;
    };

    ACTIVITY_EVENTS.forEach((e) =>
      document.addEventListener(e, markActive, { passive: true })
    );

    const flush = () => {
      if (wasActiveRef.current && document.visibilityState !== "hidden") {
        wasActiveRef.current = false;
        postHeartbeat().catch(() => {
          // Silent — a missed heartbeat is not critical
        });
      }
    };

    const timer = setInterval(flush, HEARTBEAT_INTERVAL_MS);

    // Flush remaining activity when the tab loses focus / is closed
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(timer);
    };
  }, []);
}
